/**
 * The responsive frame every screen with a sidebar sits in (task 9.10).
 *
 * Mobile-first: below `md` the sidebar is a drawer over the content, above it
 * the sidebar is always there and the drawer machinery is inert. The main
 * region is the skip link's target.
 *
 * The chat workspace passes its channel list as `sidebar`; the launch surfaces
 * pass the global navigation. The frame itself knows about neither.
 *
 * **One bar, not two.** A caller that already has a bar of its own supplies it
 * as `header`, and the frame then draws none: the workspace's channel header
 * carries the drawer toggle itself rather than being stacked under a second bar
 * that says the mission's name (design D1 of the mobile workspace redesign).
 * Two bars cost ~176px of a phone screen for three pieces of information, and
 * the saving is in merging them rather than in shrinking either.
 */
import { useEffect, type JSX, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './Button.js';
import { SIDEBAR_BREAKPOINT, useMediaQuery } from '../hooks/use-media-query.js';
import { useViewStore } from '../store/view-store.js';

/**
 * The control that opens the drawer, wherever the drawer's bar happens to be.
 *
 * Exported because the workspace's channel header absorbs it below the
 * breakpoint (design D1), and a second hand-rolled copy is a second place for
 * `aria-controls`, `aria-expanded` and the test id to drift from the sidebar
 * they describe. There is one drawer, so there is one control for it.
 */
export function DrawerToggle(): JSX.Element {
  const drawerOpen = useViewStore((state) => state.drawerOpen);
  const setDrawerOpen = useViewStore((state) => state.setDrawerOpen);
  return (
    <Button
      variant="ghost"
      onClick={() => {
        setDrawerOpen(!drawerOpen);
      }}
      aria-expanded={drawerOpen}
      aria-controls="app-sidebar"
      data-testid="drawer-toggle"
    >
      Menu
    </Button>
  );
}

export interface AppLayoutProps {
  readonly sidebar: ReactNode;
  /** Accessible name for the sidebar landmark, e.g. "Channels". */
  readonly sidebarLabel: string;
  /**
   * Shown in the frame's own mobile header bar beside the menu button. Ignored
   * when `header` is supplied, because then there is no such bar.
   */
  readonly heading?: ReactNode;
  /**
   * The caller's own bar, drawn in place of the frame's below the breakpoint.
   *
   * Supplying it replaces the frame's bar rather than adding to it, so the
   * screen has one header element between the top of the viewport and its
   * content. The node is rendered unwrapped and directly, which makes it — and
   * not this component — the element that touches the top edge: it owns
   * `pt-safe px-safe`, its own `sticky top-0 z-30`, and its own background.
   *
   * At and above the breakpoint nothing is drawn here at all, exactly as the
   * frame's own `md:hidden` bar is not. A caller with a bar for both viewports
   * renders the wide one in its own tree, where the rest of its layout is.
   */
  readonly header?: ReactNode;
  /**
   * Pins the frame to the viewport and lets the content own its own scrolling.
   * The chat workspace needs this — its feed scrolls while its header and
   * composer stay put — and the launch surfaces, which are documents, do not.
   */
  readonly fill?: boolean | undefined;
  readonly children: ReactNode;
}

export function AppLayout({
  sidebar,
  sidebarLabel,
  heading,
  header,
  fill = false,
  children,
}: AppLayoutProps): JSX.Element {
  const wide = useMediaQuery(SIDEBAR_BREAKPOINT);
  const drawerOpen = useViewStore((state) => state.drawerOpen);
  const setDrawerOpen = useViewStore((state) => state.setDrawerOpen);
  const { pathname } = useLocation();
  // Persistent above the breakpoint; a drawer below it, and hidden from the tab
  // order entirely while it is a *closed* drawer.
  const hidden = !wide && !drawerOpen;

  // Navigating is the end of the drawer's usefulness; leaving it open over the
  // new screen strands a mobile player behind a scrim they did not ask for.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname, setDrawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [drawerOpen, setDrawerOpen]);

  return (
    <div className={`flex flex-col md:flex-row ${fill ? 'h-dvh overflow-hidden' : 'min-h-dvh'}`}>
      {/*
        The frame's bar, or the caller's, or neither — never both.

        The caller's is drawn only below the breakpoint. The frame's own bar is
        `md:hidden` in CSS; a supplied one is a whole subtree with its own
        sticky treatment and its own place in the flex direction, so the same
        rule is kept in JS, where the branch already is.
      */}
      {header === undefined ? (
        <header
          data-testid="app-header"
          className="pt-safe px-safe sticky top-0 z-30 flex items-center gap-2 border-b border-line bg-surface px-3 py-2 md:hidden"
        >
          <DrawerToggle />
          <div className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{heading}</div>
        </header>
      ) : wide ? null : (
        header
      )}

      {drawerOpen && !wide ? (
        <button
          type="button"
          aria-label="Close menu"
          tabIndex={-1}
          data-testid="drawer-scrim"
          className="fixed inset-0 z-30 cursor-default bg-black/50 md:hidden"
          onClick={() => {
            setDrawerOpen(false);
          }}
        />
      ) : null}

      <aside
        id="app-sidebar"
        aria-label={sidebarLabel}
        data-testid="app-sidebar"
        data-open={hidden ? 'false' : 'true'}
        inert={hidden}
        aria-hidden={hidden}
        className={`pt-safe pb-safe pl-safe fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] overflow-y-auto border-r border-line bg-surface transition-transform md:sticky md:top-0 md:z-0 md:h-dvh md:max-w-none md:translate-x-0 ${
          hidden ? '-translate-x-full' : 'translate-x-0'
        }`}
      >
        {sidebar}
      </aside>

      <main
        id="main"
        tabIndex={-1}
        className={`px-safe min-w-0 flex-1 outline-none ${
          fill ? 'flex min-h-0 flex-col overflow-hidden' : 'pb-safe'
        }`}
        data-testid="main"
      >
        {children}
      </main>
    </div>
  );
}
